import "./style.css";
import {
  categories,
  defaultLocale,
  dictionary,
  heroProjects,
  hotKeywords,
  locales,
} from "./config";

const STORAGE_KEYS = {
  locale: "ireland-life-navigator-locale",
};

const state = {
  locale: getInitialLocale(),
  query: "",
};

const app = document.querySelector("#app");

function getInitialLocale() {
  const saved = localStorage.getItem(STORAGE_KEYS.locale);
  if (locales.some((locale) => locale.code === saved)) return saved;
  return defaultLocale;
}

function t(key) {
  return dictionary[state.locale][key] ?? dictionary[defaultLocale][key] ?? key;
}

function localize(value) {
  if (typeof value === "string") return value;
  return value?.[state.locale] ?? value?.[defaultLocale] ?? "";
}

function normalize(value) {
  return String(value).trim().toLowerCase();
}

function itemSearchText(item) {
  return normalize(
    [
      item.id,
      item.href,
      localize(item.title),
      localize(item.description),
      localize(item.status),
      ...(item.keywords ?? []),
      ...(localize(item.tags) || []),
      ...(item.items ?? []).flatMap((child) => [
        localize(child.title),
        child.href,
        child.badge,
        ...(child.keywords ?? []),
      ]),
    ]
      .filter(Boolean)
      .join(" "),
  );
}

function projectMatches(project) {
  if (!state.query) return true;
  return itemSearchText(project).includes(normalize(state.query));
}

function getFilteredCategories() {
  if (!state.query) return categories;

  const query = normalize(state.query);
  return categories
    .map((category) => {
      const categoryMatches = itemSearchText(category).includes(query);
      const items = categoryMatches
        ? category.items
        : category.items.filter((item) => itemSearchText(item).includes(query));

      return { ...category, items };
    })
    .filter((category) => category.items.length > 0);
}

function getStats() {
  const linkCount = categories.reduce((total, category) => total + category.items.length, 0);
  return [
    { value: heroProjects.length, label: t("statsProjects") },
    { value: categories.length, label: t("statsCategories") },
    { value: linkCount, label: t("statsLinks") },
  ];
}

function getHotItems() {
  return categories
    .flatMap((category) =>
      category.items.map((item) => ({
        ...item,
        categoryTitle: category.title,
      })),
    )
    .filter((item) => item.badge)
    .slice(0, 8);
}

function setLocale(locale) {
  state.locale = locale;
  localStorage.setItem(STORAGE_KEYS.locale, locale);
  document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  render();
}

function setQuery(query) {
  state.query = query;
  renderDynamicSections();
}

function renderShell() {
  app.innerHTML = `
    <div class="min-h-screen bg-[#f7fbf4] text-slate-950 antialiased">
      <header class="site-header">
        <nav class="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <a href="/" class="brand-lockup" aria-label="${t("brand")}">
            <span class="brand-logo">520</span>
            <span class="min-w-0">
              <span class="block truncate text-base font-black sm:text-lg">${t("brand")}</span>
              <span class="block truncate text-xs font-bold text-emerald-800">${t("brandHint")}</span>
            </span>
          </a>
          <div class="hidden flex-1 justify-center gap-2 md:flex">
            <a class="nav-pill" href="/">${t("navHome")}</a>
            <a class="nav-pill" href="#projects">${t("navProjects")}</a>
            <a class="nav-pill" href="#categories">${t("navCategories")}</a>
            <a class="nav-pill" href="#about">${t("navAbout")}</a>
          </div>
          <div class="ml-auto flex items-center gap-2">
            <button id="locale-toggle" class="small-button strong" type="button" aria-label="${t("languageLabel")}">
              ${state.locale === "zh" ? "EN" : "中文"}
            </button>
          </div>
        </nav>
      </header>

      <main class="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <section class="portal-hero">
          <div class="hero-copy">
            <p class="eyebrow">${t("heroEyebrow")}</p>
            <h1>${t("heroTitle")}</h1>
            <p>${t("heroDescription")}</p>
            <div class="mt-5 flex flex-wrap gap-2">
              <a href="#projects" class="primary-button">${t("primaryCta")}</a>
              <a href="#categories" class="secondary-button">${t("secondaryCta")}</a>
            </div>
          </div>
          <div class="search-board">
            <label for="global-search">${t("searchLabel")}</label>
            <div class="search-row">
              <input id="global-search" type="search" value="${escapeHtml(state.query)}" placeholder="${t("searchPlaceholder")}" autocomplete="off" />
              <button id="clear-search" type="button">${t("clearLabel")}</button>
            </div>
            <div class="hot-searches">
              <span>${t("hotSearchLabel")}</span>
              <div id="hot-keywords"></div>
            </div>
          </div>
        </section>

        <section class="stats-strip" id="stats-row"></section>

        <section class="layout-grid">
          <aside class="side-panel">
            <h2>${t("rankingTitle")}</h2>
            <div id="hot-list" class="mt-3 grid gap-2"></div>
          </aside>

          <div class="main-column">
            <section id="projects" class="portal-section">
              <div class="section-heading">
                <div>
                  <h2>${t("featuredTitle")}</h2>
                  <p>${t("featuredSubtitle")}</p>
                </div>
              </div>
              <div id="project-grid" class="project-grid"></div>
            </section>

            <section id="categories" class="portal-section">
              <div class="section-heading">
                <div>
                  <h2>${t("categoriesTitle")}</h2>
                  <p>${t("categoriesSubtitle")}</p>
                </div>
              </div>
              <div id="category-grid" class="category-grid"></div>
              <div id="empty-state" class="hidden empty-state"></div>
            </section>
          </div>
        </section>
      </main>

      <footer id="about" class="site-footer">
        ${t("footerText")}
      </footer>
    </div>
  `;
}

function render() {
  document.documentElement.classList.remove("dark");
  renderShell();
  renderStaticSections();
  renderDynamicSections();
  bindEvents();
}

function renderStaticSections() {
  document.querySelector("#stats-row").innerHTML = getStats()
    .map(
      (stat) => `
        <div class="stat-card">
          <strong>${stat.value}</strong>
          <span>${stat.label}</span>
        </div>
      `,
    )
    .join("");

  document.querySelector("#hot-keywords").innerHTML = hotKeywords
    .map(
      (keyword) => `
        <button class="keyword-chip" type="button" data-query="${escapeHtml(keyword.query)}">
          ${localize(keyword)}
        </button>
      `,
    )
    .join("");

  document.querySelector("#hot-list").innerHTML = getHotItems()
    .map(
      (item, index) => `
        <a href="${item.href}" class="rank-link">
          <span>${String(index + 1).padStart(2, "0")}</span>
          <strong>${localize(item.title)}</strong>
        </a>
      `,
    )
    .join("");
}

function renderDynamicSections() {
  renderProjects();
  renderCategories();
  const searchInput = document.querySelector("#global-search");
  if (searchInput && searchInput.value !== state.query) {
    searchInput.value = state.query;
  }
}

function renderProjects() {
  const projects = heroProjects.filter(projectMatches);
  document.querySelector("#project-grid").innerHTML = projects
    .map(
      (project) => `
        <a href="${project.href}" class="project-card accent-${project.accent}">
          <span class="rank-badge">${project.rank}</span>
          <span class="status-pill">${localize(project.status)}</span>
          ${renderThumbnail(project.thumbnail, "project")}
          <strong>${localize(project.title)}</strong>
          <p>${localize(project.description)}</p>
          <span class="play-button">${t("openLabel")}</span>
          <span class="tag-row">
            ${localize(project.tags)
              .map((tag) => `<small>${tag}</small>`)
              .join("")}
          </span>
        </a>
      `,
    )
    .join("");
}

function renderCategories() {
  const filteredCategories = getFilteredCategories();
  const emptyState = document.querySelector("#empty-state");
  document.querySelector("#category-grid").innerHTML = filteredCategories
    .map(
      (category) => `
        <article class="category-card tone-${category.color}">
          <div class="category-title">
            ${renderThumbnail(category.thumbnail, "category")}
            <div>
              <h3>${localize(category.title)}</h3>
              <p>${localize(category.description)}</p>
            </div>
          </div>
          <div class="directory-grid">
            ${category.items
              .map(
                (item) => `
                  <a href="${item.href}" class="directory-link">
                    <strong>${localize(item.title)}</strong>
                    ${item.badge ? `<em>${item.badge}</em>` : ""}
                  </a>
                `,
              )
              .join("")}
          </div>
        </article>
      `,
    )
    .join("");

  const hasAnyResult = heroProjects.some(projectMatches) || filteredCategories.length > 0;
  emptyState.classList.toggle("hidden", hasAnyResult);
  emptyState.innerHTML = `
    <h3>${t("emptyTitle")}</h3>
    <p>${t("emptyDescription")}</p>
  `;
}

function renderThumbnail(type, size = "project") {
  const icon = thumbnails[type] ?? thumbnails.default;
  return `
    <span class="${size === "project" ? "project-thumbnail" : "category-thumbnail"}" aria-hidden="true">
      ${icon}
    </span>
  `;
}

const thumbnails = {
  safety: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M18 24h60a9 9 0 0 1 9 9v39a9 9 0 0 1-9 9H18a9 9 0 0 1-9-9V33a9 9 0 0 1 9-9Z"/>
      <path class="thumb-land" d="M13 69c13-10 24-5 35-13 14-10 25-8 35 4v12a8 8 0 0 1-8 8H21a8 8 0 0 1-8-8v-3Z"/>
      <path class="thumb-orange" d="M48 17 69 27v17c0 17-10 28-21 34-11-6-21-17-21-34V27l21-10Z"/>
      <path class="thumb-white" d="M48 27 60 32v12c0 10-5 17-12 22-7-5-12-12-12-22V32l12-5Z"/>
      <path class="thumb-green" d="M43 48h10v14H43zM38 42l10-8 10 8v4H38z"/>
      <circle class="thumb-dot" cx="24" cy="37" r="4"/>
      <circle class="thumb-dot" cx="73" cy="53" r="3"/>
    </svg>
  `,
  insurance: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M18 28h60a8 8 0 0 1 8 8v36a8 8 0 0 1-8 8H18a8 8 0 0 1-8-8V36a8 8 0 0 1 8-8Z"/>
      <path class="thumb-orange" d="M48 16c16 0 29 12 29 27v5H19v-5c0-15 13-27 29-27Z"/>
      <path class="thumb-white" d="M23 48h50v27H23z"/>
      <path class="thumb-green" d="M31 57h34v6H31zM31 67h23v5H31z"/>
      <path class="thumb-orange" d="M65 57h12v16H65z"/>
      <path class="thumb-dark" d="M18 48h60v6H18z"/>
      <circle class="thumb-dot" cx="48" cy="40" r="5"/>
    </svg>
  `,
  settle: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M18 22h60a8 8 0 0 1 8 8v45a8 8 0 0 1-8 8H18a8 8 0 0 1-8-8V30a8 8 0 0 1 8-8Z"/>
      <path class="thumb-white" d="M25 23h46v58H25z"/>
      <path class="thumb-green" d="M32 34h24v6H32zM32 48h32v5H32zM32 59h25v5H32z"/>
      <path class="thumb-orange" d="M63 32h14v14H63zM61 60l8-8 8 8v15H61z"/>
      <path class="thumb-dark" d="M66 66h6v9h-6z"/>
      <circle class="thumb-dot" cx="29" cy="49" r="3"/>
      <circle class="thumb-dot" cx="29" cy="60" r="3"/>
    </svg>
  `,
  rent: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M16 39 48 15l32 24v37a7 7 0 0 1-7 7H23a7 7 0 0 1-7-7V39Z"/>
      <path class="thumb-orange" d="M11 41 48 13l37 28-5 7-32-24-32 24-5-7Z"/>
      <path class="thumb-white" d="M29 46h38v35H29z"/>
      <path class="thumb-green" d="M36 56h10v10H36zM51 56h10v10H51z"/>
      <path class="thumb-dark" d="M43 69h10v12H43z"/>
      <circle class="thumb-dot" cx="56" cy="75" r="2"/>
    </svg>
  `,
  daily: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M16 29h64a8 8 0 0 1 8 8v27a8 8 0 0 1-8 8H16a8 8 0 0 1-8-8V37a8 8 0 0 1 8-8Z"/>
      <path class="thumb-green" d="M18 39h60v18H18z"/>
      <path class="thumb-white" d="M25 36h12v20H25zM45 36h12v20H45z"/>
      <circle class="thumb-dark" cx="28" cy="72" r="7"/>
      <circle class="thumb-dark" cx="68" cy="72" r="7"/>
      <path class="thumb-orange" d="M59 18h13v11H59z"/>
    </svg>
  `,
  finance: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M19 25h58a8 8 0 0 1 8 8v42a8 8 0 0 1-8 8H19a8 8 0 0 1-8-8V33a8 8 0 0 1 8-8Z"/>
      <path class="thumb-white" d="M22 36h52v34H22z"/>
      <path class="thumb-green" d="M25 42h46v10H25z"/>
      <path class="thumb-orange" d="M31 58h10v12H31zM45 54h10v16H45zM59 49h10v21H59z"/>
      <path class="thumb-dark" d="M27 30h42v6H27z"/>
    </svg>
  `,
  housing: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M13 46 48 19l35 27v30a7 7 0 0 1-7 7H20a7 7 0 0 1-7-7V46Z"/>
      <path class="thumb-green" d="M21 48h54v35H21z"/>
      <path class="thumb-white" d="M33 57h13v12H33zM51 57h13v12H51z"/>
      <path class="thumb-orange" d="M48 18 86 47l-6 8-32-24-32 24-6-8 38-29Z"/>
      <path class="thumb-dark" d="M43 70h10v13H43z"/>
    </svg>
  `,
  work: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M18 29h60a8 8 0 0 1 8 8v35a8 8 0 0 1-8 8H18a8 8 0 0 1-8-8V37a8 8 0 0 1 8-8Z"/>
      <path class="thumb-orange" d="M35 21h26a6 6 0 0 1 6 6v8H29v-8a6 6 0 0 1 6-6Zm5 8v6h16v-6H40Z"/>
      <path class="thumb-green" d="M12 48h72v14H12z"/>
      <path class="thumb-white" d="M42 45h12v13H42z"/>
      <path class="thumb-dark" d="M19 65h58v6H19z"/>
    </svg>
  `,
  travel: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M14 20h68a7 7 0 0 1 7 7v46a7 7 0 0 1-7 7H14a7 7 0 0 1-7-7V27a7 7 0 0 1 7-7Z"/>
      <path class="thumb-white" d="M17 58c18-12 27-8 38-17 9-7 17-8 28-1v33H17V58Z"/>
      <path class="thumb-green" d="M20 64c13-7 25-3 35-10 8-5 16-5 26 2v17H20v-9Z"/>
      <path class="thumb-orange" d="M47 22l25 42-8 3-14-17-2 22-8 1-2-23-15 17-7-4 31-41Z"/>
      <circle class="thumb-dot" cx="26" cy="34" r="5"/>
    </svg>
  `,
  tools: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M19 17h58a7 7 0 0 1 7 7v48a7 7 0 0 1-7 7H19a7 7 0 0 1-7-7V24a7 7 0 0 1 7-7Z"/>
      <path class="thumb-white" d="M25 27h46v12H25zM25 46h12v12H25zM42 46h12v12H42zM59 46h12v12H59zM25 63h29v8H25z"/>
      <path class="thumb-green" d="M60 61 76 45l7 7-16 16-9 2 2-9Z"/>
      <path class="thumb-orange" d="M72 41l5-5 7 7-5 5z"/>
    </svg>
  `,
  default: `
    <svg viewBox="0 0 96 96" role="img">
      <path class="thumb-sky" d="M18 18h60a8 8 0 0 1 8 8v52a8 8 0 0 1-8 8H18a8 8 0 0 1-8-8V26a8 8 0 0 1 8-8Z"/>
      <path class="thumb-green" d="M28 52h40v24H28z"/>
      <path class="thumb-orange" d="M48 24 73 52H23z"/>
      <path class="thumb-white" d="M42 57h12v19H42z"/>
    </svg>
  `,
};

function bindEvents() {
  document.querySelector("#global-search").addEventListener("input", (event) => {
    setQuery(event.target.value);
  });

  document.querySelector("#clear-search").addEventListener("click", () => {
    setQuery("");
    document.querySelector("#global-search").focus();
  });

  document.querySelectorAll(".keyword-chip").forEach((button) => {
    button.addEventListener("click", () => {
      setQuery(button.dataset.query);
      document.querySelector("#global-search").focus();
    });
  });

  document.querySelector("#locale-toggle").addEventListener("click", () => {
    setLocale(state.locale === "zh" ? "en" : "zh");
  });

}

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

render();
